import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import dynamic from "next/dynamic";
import classNames from "classnames";
import { useWindowSize } from "@src/hooks/useWindowsSize";
import { showEnterForm } from "@app/store/layoutSlice";
import { useGetRecommendedOffersQuery } from "@app/store/offersApi";
import { TwoColumnsProductCardBP } from "@app/styles/layout-vars";
import ProductChartLine from "@features/charts/ProductChartLine/ProductChartLine";
import { Gallery, SimilarProducts } from "@features/product";
import ProductContactCard from "@features/product/product-contact-card/product-contact-card";
import { ProductMyOffer } from "@features/product/product-my-offer";
import { getBreadcrumbItemsFromMeta, getProductTitle, ProductError } from "@entities/product";
import { useScroll } from "@shared/lib/hooks/useScroll";
import { AdBlock } from "@shared/ui/AdBlock";
import { AD_BLOCK_6_ID, AD_BLOCK_6_OWNER_ID } from "@shared/ui/AdBlock/constants";
import { Breadcrumbs } from "@shared/ui/Breadcrumbs";
import ProductDescription from "../product-description/product-description";
import ProductLocation from "../product-location/product-location";
import ProductProps from "../product-props/product-props";
import { SCROLL_TRIGGER, SHOW_ENTER_FORM_MESSAGE } from "./constants";
import { updateCounterViews } from "./lib/offerViewCounter";
import { ProductScreenProps } from "./types";
import classes from "./product-screen.module.scss";

const MotionDiv = dynamic(() => import("framer-motion").then((mod) => mod.motion.div), {
    ssr: false
});

const DynamicCardChart = dynamic(() => import("@features/charts/CardChart/CardChart"), {
    ssr: false
});

const ProductScreen = ({ className, preloadedData, meta, sellerData }: ProductScreenProps) => {
    const preloadError = !preloadedData || !preloadedData.id;
    const hasError = !preloadError && preloadedData;

    const dispatch = useDispatch();

    const { data: recommendedOffers, isFetching: recommendedOffersIsFetching } =
        useGetRecommendedOffersQuery(preloadedData?.id, {
            skip: !preloadedData?.id
        });

    const size = useWindowSize();
    const [isSticked, setIsSticked] = useState(true);
    const variants = {
        sticked: { y: [0, 5, 0] },
        standart: { y: 0 }
    };

    const [isOpen, setIsOpen] = useState(false);
    const { scrollY } = useScroll();
    scrollY.onChange((y) => setIsOpen(y > SCROLL_TRIGGER && !isOpen));

    useEffect(() => {
        if (size.width) {
            if (size.width < TwoColumnsProductCardBP) {
                setIsSticked(false);
            } else {
                setIsSticked(true);
            }
        }
    });

    useEffect(() => {
        if (typeof window !== "undefined") {
            const views = updateCounterViews();
            if (views === 10) {
                dispatch(showEnterForm(SHOW_ENTER_FORM_MESSAGE));
            }
        }
    }, []);

    return (
        <div className={classNames(classes.product, className)}>
            {hasError ? (
                <>
                    <Breadcrumbs
                        prevItems={getBreadcrumbItemsFromMeta(meta)}
                        title={preloadedData?.title || ""}
                    />
                    <div className={classes.product__chart}>
                        <ProductChartLine offer={preloadedData} />
                    </div>
                    <h1 className={classes.product__title}>{getProductTitle(preloadedData)}</h1>
                    <div className={classes.product__inner}>
                        <Gallery className={classes.product__gallery} offer={preloadedData} />
                        <div className={classes.product__content}>
                            <ProductProps offer={preloadedData} />
                            <ProductDescription offer={preloadedData} />
                            <ProductLocation offer={preloadedData} />
                            <DynamicCardChart offer={preloadedData} maxSlices={6} />
                        </div>
                        <MotionDiv
                            className={classes.product__aside}
                            animate={isOpen && isSticked ? "sticked" : "standart"}
                            variants={variants}
                            transition={{
                                type: "spring",
                                damping: 5,
                                stiffness: 50,
                                restDelta: 0.001,
                                duration: 0.34
                            }}
                        >
                            <ProductMyOffer offer={preloadedData} />
                            <ProductContactCard offer={preloadedData} sellerData={sellerData} />
                            <AdBlock
                                isAdfox={true}
                                ownerId={AD_BLOCK_6_OWNER_ID}
                                blockId={AD_BLOCK_6_ID}
                            />
                        </MotionDiv>
                    </div>
                    <SimilarProducts
                        data={recommendedOffers?.offers || []}
                        isLoading={recommendedOffersIsFetching}
                    />
                </>
            ) : (
                <ProductError />
            )}
        </div>
    );
};
export default ProductScreen;